import "../style.scss"
import "./styles.scss"

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import { CircularProgress } from "@mui/material"
import Backdrop from "@mui/material/Backdrop"
import _get from "lodash/get"
import moment from "moment"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import AccessControl from "../../../components/access/AccessControl"
import PasswordProtected from "../../../components/access/PasswordProtected"
import CheckoutKit from "../../../components/checkout-kit"
import Error from "../../../components/error"
import EventMap from "../../../components/event-map"
import Head from "../../../components/head"
import Layout from "../../../components/layout"
import { ENV } from "../../../constants/env"
import { getEvent } from "../../../http_service"
import { configureCheckoutKit } from "../../../utils/checkout-kit-config"
import { formatDate, formatTime } from "../../../utils/common/format-date"
import { eventJsonLd } from "../../../utils/seo/structured-data"
import NotFoundPage from "../../404"

const cookie = require("cookie")

const isWindowDefined = typeof window !== "undefined"

configureCheckoutKit()

// Below this an organiser's description is too thin to be the search snippet.
const MIN_DESCRIPTION_LENGTH = 70
const MAX_DESCRIPTION_LENGTH = 160

const truncateAtWord = (text, max) => {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(" ")
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.—-]+$/, "")}…`
}

const EventPage = ({ serverData }) => {
  const { t, i18n } = useTranslation(["meta", "events"])
  const locale = i18n.resolvedLanguage
  const [eventData, setEventData] = useState({
    event: {},
    venue: {},
    slug: "",
    pk: "",
    tickets: []
  })

  const [uiState, setUiState] = useState({
    loading: false,
    showBillingInfo: false,
    isHydrated: false,
    queryPromoCode: "",
    showFloatingButton: false
  })

  const eventSlug = serverData?.slug
  const eventId = serverData?.eventId
  const isPasswordProtected = serverData?.error && serverData?.message?.status === 401
  const handleCheckoutViewChange = useCallback(view => {
    const showBillingInfo = view === "checkout"
    setUiState(prev =>
      prev.showBillingInfo === showBillingInfo
        ? prev
        : { ...prev, showBillingInfo }
    )
  }, [])

  useEffect(() => {
      setUiState(prev => ({
        ...prev,
        isHydrated: true,
        queryPromoCode: new URL(window.location.href).searchParams.get("r") || ""
      }))
  }, [])

  // Floating button visibility logic for mobile
  useEffect(() => {
    if (!isWindowDefined || window.innerWidth >= 768) return

    const baseWidth = window.innerWidth

    const handleScroll = () => {
      const { activeElement } = document
      const isBillingFieldFocused =
        uiState.showBillingInfo &&
        activeElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName) &&
        activeElement.closest(".tf-checkout-flow__checkout")

      // Avoid UI state churn while iOS keyboard opens/closes on billing fields.
      if (isBillingFieldFocused) {
        return
      }

      // Check if billing info is showing
      if (uiState.showBillingInfo) {
        const billingInfo = document.querySelector('.tf-checkout-flow__checkout')
        if (!billingInfo) {
          setUiState(prev => ({ ...prev, showFloatingButton: true }))
          return
        }
        const billingRect = billingInfo.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Show button if billing info is not in view (top is below viewport or mostly above it)
        const isInView = billingRect.top < viewportHeight * 0.8 && billingRect.bottom > 100
        setUiState(prev => ({ ...prev, showFloatingButton: !isInView }))
      } else {
        // Check tickets wrapper visibility
        const ticketsWrapper = document.querySelector('.tickets-wrapper')
        if (!ticketsWrapper) {
          setUiState(prev => ({ ...prev, showFloatingButton: false }))
          return
        }
        const ticketsRect = ticketsWrapper.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Show button if tickets wrapper is not in view
        const isInView = ticketsRect.top < viewportHeight * 0.8 && ticketsRect.bottom > 100
        setUiState(prev => ({ ...prev, showFloatingButton: !isInView }))
      }
    }

    const handleResize = () => {
      // Ignore height-only changes — these are caused by the mobile keyboard
      // opening/closing and should not trigger a re-render that disrupts input focus
      if (window.innerWidth !== baseWidth) {
        handleScroll()
      }
    }

    handleScroll() // Check on mount
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [uiState.isHydrated, uiState.showBillingInfo])

  const scrollToTicketsOrBilling = () => {
    if (uiState.showBillingInfo) {
      const billingInfo = document.querySelector('.tf-checkout-flow__checkout')
      if (billingInfo) {
        const yOffset = -20 // 20px offset from top
        const y = billingInfo.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    } else {
      const ticketsWrapper = document.querySelector('.tickets-wrapper')
      if (ticketsWrapper) {
        const yOffset = -20 // 20px offset from top
        const y = ticketsWrapper.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }
  }

  // Scroll to billing info when it becomes visible
  useEffect(() => {
    if (uiState.showBillingInfo && isWindowDefined) {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        const billingInfo = document.querySelector('.tf-checkout-flow__checkout')
        if (billingInfo) {
          const yOffset = -20 // 20px offset from top for better visibility
          const y = billingInfo.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      }, 150)
    }
  }, [uiState.showBillingInfo])

  useEffect(() => {
    if (serverData) {
      setEventData({
        event: _get(serverData, "event.event", {}),
        venue: _get(serverData, "event.venue", {}),
        slug: _get(serverData, "slug", ""),
        pk: _get(serverData, "pk", "")
      })
    }
  }, [serverData])

  if (serverData.showErrorPage) {
    return <NotFoundPage />
  }

  if (serverData.storedPassword !== ENV.TTF_ACCESS && serverData.host?.includes("tickets-staging.manacommon.com")) {
    return <AccessControl />
  }

  const event = isPasswordProtected ? eventData.event : _get(serverData, "event.event", {})
  const { name, start_date, end_date, image_full_url, description, description_plain, id } = event

  // Fix description images to respect their width/height attributes
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!description || !isWindowDefined) return

    const descriptionContainer = document.querySelector('.description-container')
    if (!descriptionContainer) return

    const images = descriptionContainer.querySelectorAll('img[width], img[height]')
    images.forEach(img => {
      const width = img.getAttribute('width')
      const height = img.getAttribute('height')

      if (width) {
        img.style.width = `${width}px`
        img.style.maxWidth = '100%'
      }
      if (height) {
        img.style.height = `${height}px`
      }
    })
  }, [description])

  const date = formatDate(start_date, locale)
  const startHour = formatTime(start_date, locale)
  const endHour = formatTime(end_date, locale)

  const getAuthorizedEvent = async () => {
    setUiState(prev => ({ ...prev, loading: true }))
    try {
      const response = await getEvent(eventData.slug, eventData.pk)
      setEventData({
        event: _get(response, "event"),
        venue: _get(response, "venue", {}),
        slug: _get(response, "slug", ""),
        pk: _get(response, "pk", "")
      })
    } catch (e) {
    } finally {
      setUiState(prev => ({ ...prev, loading: false }))
    }
  }

  if (uiState.loading) {
    return (
      <Backdrop sx={{ color: "#fff", backgroundColor: "transparent", zIndex: 1205 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  if (serverData.error && Object.keys(eventData.event).length === 0) {
    if (serverData.message.status === 401) {
      return (
        <Layout>
          <Head noindex />
          <PasswordProtected
            slug={eventData.slug || eventSlug}
            pk={eventData.pk}
            id={id || eventId}
            getAuthorizedEvent={getAuthorizedEvent}
          />
        </Layout>
      )
    }
    return <Error />
  }

  const venue = isPasswordProtected ? eventData.venue : _get(serverData, "event.venue", {})
  const metaValues = { name, date, venue: venue.name }

  // The organiser's own copy leads when there is enough of it to describe the
  // event. Many descriptions are a line or two ("VICTOR RUIZ"), which makes a
  // useless search snippet, so those are followed by the what/when/where
  // sentence instead of standing alone.
  const plainDescription = (description_plain || (description || "").replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim()
  const templateDescription = t("meta:event.description", metaValues)
  const eventDescription = truncateAtWord(
    plainDescription.length >= MIN_DESCRIPTION_LENGTH
      ? plainDescription
      : [plainDescription, templateDescription].filter(Boolean).join(" — "),
    MAX_DESCRIPTION_LENGTH
  )

  const eventKeywords = t("meta:event.keywords", metaValues)
  const eventPath = `/events/${serverData.slug || eventData.slug}/`
  const eventUrl = `${ENV.CANONICAL_ORIGIN}${eventPath}`

  // Not emitted for a password-protected event: its details are not public.
  const jsonLd = isPasswordProtected
    ? null
    : eventJsonLd({
        event,
        venue,
        tickets: _get(serverData, "event.tickets", []),
        tables: _get(serverData, "event.tables", []),
        url: eventUrl,
        siteUrl: `${ENV.CANONICAL_ORIGIN}/events/`
      })

  return (
    <>
      <Head
        title={t("meta:event.title", metaValues)}
        description={eventDescription}
        image={image_full_url}
        keywords={eventKeywords}
        pathname={eventPath}
        noindex={isPasswordProtected}
        jsonLd={jsonLd}
      />
      <Layout>
        {/* Structured data is the JSON-LD in <Head>. The microdata that used to
            sit on these elements described the same event a second time, with a
            display string for startDate and a `startHour` property that does
            not exist — both of which invalidate the item. */}
        <div className="event-details-container">
          {/* Mobile event image - shown only on mobile */}
          <div className="mobile-event-image-container">
            {image_full_url && (
              <img
                src={image_full_url}
                alt={t("events:detail.flyerAlt", { name })}
                onError={({ currentTarget }) => {
                  currentTarget.setAttribute('style', 'display: none;')
                }}
              />
            )}
          </div>

          {/* Event info header */}
          <div className="info-container">
            <h1 className="name">
              {name}
            </h1>
            <p className="date-venue">
              <span className="event-when">{ date } </span>
              <span className="event-when">{ startHour } - </span>
              <span className="event-when">{ endHour }</span>
            </p>
            <p className="date-venue">
              <span className="event-where">
                <LocationOnOutlinedIcon />
                <span>{venue.name}</span>
              </span>
            </p>
          </div>

          {/* Main content container */}
          <div className="main-content-container">
            {/* Description column - LEFT */}
            <div className="description-container">
              {description ? (
                <div dangerouslySetInnerHTML={{ __html: description }} />
              ) : (
                <div className="no-description">
                  <p>{t("events:detail.noDescription")}</p>
                </div>
              )}

              {/* Map below description */}
              <EventMap venue={venue} />
            </div>

            {/* Tickets and image column - RIGHT */}
            <div className="mn__event-tickets-container">
              {/* Desktop event image - ABOVE tickets */}
              <div className="event-image-container">
                {image_full_url && (
                  <img
                    src={image_full_url}
                    alt={t("events:detail.flyerAlt", { name })}
                    onError={({ currentTarget }) => {
                      currentTarget.setAttribute('style', 'display: none;')
                    }}
                  />
                )}
              </div>

              {/* Tickets section - BELOW image */}
              <div className="tickets-wrapper">
                <CheckoutKit
                  eventId={id || eventId}
                  initialPromoCode={uiState.queryPromoCode}
                  onViewChange={handleCheckoutViewChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Floating Get Tickets/Checkout Button - Mobile Only */}
        {uiState.isHydrated && uiState.showFloatingButton && (
          <button type="button" className="floating-get-tickets-btn" onClick={scrollToTicketsOrBilling}>
            {uiState.showBillingInfo ? t("events:detail.goToCheckout") : t("events:detail.getTickets")}
          </button>
        )}
      </Layout>
    </>
  )
}

export async function getServerData(props) {
  const cookies = props.headers.get("cookie")
  let storedPassword = ""
  if (cookies) {
    storedPassword = cookie.parse(cookies)?.["ttf-access"] || ""
  }

  let slug = props.params?.slug || ""

  if (!slug && props.url) {
    const urlMatch = props.url.match(/\/events\/([^/]+)/)
    if (urlMatch) {
      const [, extractedSlug] = urlMatch
      if (extractedSlug) {
        slug = extractedSlug
      }
    }
  }

  // Defensive checks for slug
  if (!slug) {
    console.error('[SSR] Missing slug in params and could not extract from URL:', props)
    return {
      status: 404,
      props: {
        showErrorPage: true
      }
    }
  }

  const {
    query: { pk, eventId }
  } = props

  try {
    const event = await getEvent(slug, pk)
    const { end_date } = event.event

    const endDateUnindex = moment(end_date).add(2, "days")
    const isAfterEndDate = moment().isAfter(endDateUnindex)

    if (isAfterEndDate) {
      return {
        status: 404,
        props: {
          showErrorPage: true
        }
      }
    }

    return {
      props: {
        event,
        slug,
        pk,
        eventId,
        storedPassword,
        host: props.headers.get("host")
      }
    }
  } catch (error) {
    const status = error?.response?.status

    // An event the API does not know is a real 404, not a 200 carrying an
    // error message — search engines index the latter as a page.
    if (status === 404) {
      return {
        status: 404,
        props: {
          showErrorPage: true
        }
      }
    }

    return {
      // 401 is a page that works (the password prompt). Anything else is the
      // API failing, which must not be indexed as this event's content.
      status: status === 401 ? 200 : 503,
      props: {
        error: true,
        // Only the status crosses to the browser. Passing the axios error
        // itself serialised its whole request config — URL, headers, stack
        // trace — into the page for anyone to read.
        message: { status },
        slug,
        pk,
        eventId: error?.response?.data?.event_id
      }
    }
  }
}

export default EventPage
